import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Form,
  Button,
  Tooltip,
  Typography,
  Table,
  Breadcrumb,
  Result,
} from "antd";
import { DownloadOutlined} from "@ant-design/icons";
import get from "lodash/get";
import map from "lodash/map";
import dayjs from "dayjs";
import { getInjectionMouldingDayColumns } from "../../constants/tableColumns/summaryReportsColumns/daySummaryColumns/getInjectionMouldingDayColumns";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { CalendarOutlined, BorderlessTableOutlined } from "@ant-design/icons";
import { fetchDaySummaryReport } from "../../redux/actions/reportActions";
import { fetchDaySummaryReportPdf } from "../../redux/actions/downloadPdfAction";
import { getCurrentDateRange } from "../../utilities/report";
import TableSummary from "../../components/reports/daySummaryReport/tableSummary";
import HeaderSelector from "../../components/reports/daySummaryReport/headerSelector";
import requireAuth from "../../hocs/requireAuth";
import { useExcelExport } from "../../hooks/useExcelExport";
import ExcelExportButton from "../../components/breakdownSetting/ExcelExportButton";
import { COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";
import daySummaryColumns from "../../constants/tableColumns/summaryReportsColumns/daySummaryColumns";
import { SUPER_ADMIN_ROLE_ACCESS, ORGANISATION_ADMIN_ROLE_IDs, ORGANISATION_MANAGER_IDs, ORGANISATION_SUPERVISORS_IDs } from "../../constants/userRoles";

const { Title } = Typography;

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <CalendarOutlined style={{ fontSize: "16px" }} />
              <span>Day</span>
            </>
          ),
        },
        {
          title: "Summary Report",
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
  templateType: undefined,
  interval: "week",
  date: getCurrentDateRange("week", dayjs()),
};

const DaySummaryReportsScreen = () => {
  const [loading, setLoading] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const [machinesSummary, setMachinesSummary] = useState([]);
  const dispatch = useDispatch();
  const [config, setConfig] = useState(null);
  const [form] = Form.useForm();
  const [filter, setFilters] = useState(initialState);
  const { wrapperRef, onDownload, isReady } = useExcelExport(
    [machinesSummary, getInjectionMouldingDayColumns],
    `Day-Summary-${get(filter, "date.0", "")}-${get(filter, "date.1", "")}`,
    `Day-Summary`
  );

  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });

    setConfig({
      ...config,
      companies: [company],
      templates: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      template: undefined,
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getSummary = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      company_uuid: get(filter, "company.id"),
      from_date: get(filter, "date.0"),
      to_date: get(filter, "date.1"),
    };
    dispatch(fetchDaySummaryReport(query))
      .then((result) => {
        if (result.length > 0) {
          setShowEmpty(false);
          setMachinesSummary(result);
        } else {
          setMachinesSummary([]);
          setShowEmpty(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDownloadReport = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      company_uuid: get(filter, "company.id"),
      from_date: get(filter, "date.0"),
      to_date: get(filter, "date.1"),
    };
    dispatch(fetchDaySummaryReportPdf(query))
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);

  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>
      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto  pb-5 min-w-full">
        <Form form={form}>
          <Spin spinning={loading}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setConfig={setConfig}
              setFilters={setFilters}
              getSummary={getSummary}
              setMachinesSummary={setMachinesSummary}
            />

            {get(machinesSummary, "length") > 0 && (
              <div className="w-full px-8 overflow-y-auto">
                <div className="float-right mb-5 space-x-2">
                  <Tooltip className="mb-5" title="Download Report">
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => onDownloadReport()}
                    />
                  </Tooltip>
                  <Tooltip title="Excel Summary Report">
                    <ExcelExportButton
                      onDownload={onDownload}
                      disabled={!isReady}
                    />
                  </Tooltip>
                </div>

                <div ref={wrapperRef} className="w-full overflow-y-auto">
                  {map(machinesSummary, (summary, index) => {
                    return (
                      <div className="w-full table-container" key={index}>
                        <Title className="mt-4" level={5}>
                          {get(summary, "name")}
                        </Title>
                        <Table
                          className="mb-20 custom-horizontal-scrollbar"
                          dataSource={get(summary, "shifts")}
                          columns={daySummaryColumns(get(filter, 'templateType'))}
                          bordered
                          pagination={false}
                          summary={() => <TableSummary summary={summary} templateType={get(filter, 'templateType')} />}
                         rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {showEmpty && (
              <div className="px-8 py-8">
                <Result
                  title={`No template exists...!`}
                  subTitle="Please try again with another company or template type."
                  status="404"
                />
              </div>
            )}
          </Spin>
        </Form>
      </div>
    </div>
  );
};

export default requireAuth(DaySummaryReportsScreen,[
  ...SUPER_ADMIN_ROLE_ACCESS,
  ...ORGANISATION_ADMIN_ROLE_IDs,
  ...ORGANISATION_MANAGER_IDs,
  ...ORGANISATION_SUPERVISORS_IDs
]);
